import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { createAlarmTopic, deleteAlarmTopic, fetchAlarmTopics, updateAlarmTopic } from '@/features/alarmTopics/api'
import type { AlarmTopic, AlarmTopicFormFields } from '@/features/alarmTopics/types'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

const EMPTY_FORM: AlarmTopicFormFields = {
  alarm_real_name: '',
  alarm_code_name: '',
  alarm_severity_id: 0,
  alarm_glue: '00:01:00',
  alarm_description: '',
  alarm_cause: '',
  alarm_corrective_measure: '',
  requires_acknowledgement: false,
}

/** Ports the live admin_management.alarm_topics_management page — the
 * settings.html {% if False %} copy of this feature is dead code, not
 * replicated here. Reads GET /portal/settings/alarm-topics (pre-existing,
 * extended with an additive `alarm_severities` field); writes hit the
 * pre-existing create/update/delete JSON endpoints under the same
 * settings.alarm_topics permission the backend already enforces. */
export default function AlarmTopicsPage() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('settings.alarm_topics')
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['alarm-topics'],
    queryFn: fetchAlarmTopics,
  })

  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [form, setForm] = useState<AlarmTopicFormFields>(EMPTY_FORM)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['alarm-topics'] })

  const createMutation = useMutation({
    mutationFn: () => createAlarmTopic(form),
    onSuccess: () => {
      setEditingId(null)
      invalidate()
    },
  })
  const updateMutation = useMutation({
    mutationFn: (id: number) => updateAlarmTopic(id, form),
    onSuccess: () => {
      setEditingId(null)
      invalidate()
    },
  })
  const deleteMutation = useMutation({ mutationFn: deleteAlarmTopic, onSuccess: invalidate })

  function startEdit(topic: AlarmTopic) {
    setForm({
      alarm_real_name: topic.alarm_real_name,
      alarm_code_name: topic.alarm_code_name,
      alarm_severity_id: topic.alarm_severity_id,
      alarm_glue: topic.alarm_glue ?? '00:01:00',
      alarm_description: topic.alarm_description ?? '',
      alarm_cause: topic.alarm_cause ?? '',
      alarm_corrective_measure: topic.alarm_corrective_measure ?? '',
      requires_acknowledgement: Boolean(topic.requires_acknowledgement),
    })
    setEditingId(topic.alarm_topic_id)
  }

  function startCreate() {
    setForm(EMPTY_FORM)
    setEditingId('new')
  }

  if (isLoading) return <LoadingState label="Loading alarm topics…" />
  if (isError) {
    return (
      <ErrorState message={error instanceof Error ? error.message : 'Failed to load.'} onRetry={() => refetch()} />
    )
  }
  if (!data) return null

  const filtered = data.alarm_topics.filter((t) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      t.alarm_real_name.toLowerCase().includes(q) ||
      t.alarm_code_name.toLowerCase().includes(q) ||
      t.alarm_severity_name.toLowerCase().includes(q)
    )
  })

  const isFormOpen = editingId !== null

  return (
    // Same shrink-0-header / flex-1-scroller split as DashboardPage: fills
    // AppLayout's <main> exactly (h-full) so the table is the ONLY thing
    // that scrolls, with the title/search/form staying pinned.
    <div className="flex h-full flex-col gap-4">
      <div className="shrink-0 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-ink">Alarm Topics</h1>
        {canManage && (
          <button
            type="button"
            onClick={startCreate}
            className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            + New Topic
          </button>
        )}
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search…"
        className="w-full max-w-sm rounded-full border border-transparent bg-shell px-[18px] py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
      />

      {isFormOpen && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (editingId === 'new') createMutation.mutate()
            else if (editingId !== null) updateMutation.mutate(editingId)
          }}
          className="space-y-3 rounded-2xl border border-brand bg-card p-5"
        >
          <h2 className="text-base font-semibold text-ink">
            {editingId === 'new' ? 'Create Alarm Topic' : 'Edit Alarm Topic'}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField label="Alarm Name" value={form.alarm_real_name} onChange={(v) => setForm({ ...form, alarm_real_name: v })} />
            <TextField label="Code Name" value={form.alarm_code_name} onChange={(v) => setForm({ ...form, alarm_code_name: v })} />
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Severity</label>
              <select
                value={form.alarm_severity_id}
                onChange={(e) => setForm({ ...form, alarm_severity_id: Number(e.target.value) })}
                className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
              >
                <option value={0}>Select…</option>
                {data.alarm_severities.map((s) => (
                  <option key={s.alarm_severity_id} value={s.alarm_severity_id}>
                    {s.alarm_severity_name}
                  </option>
                ))}
              </select>
            </div>
            <TextField label="Glue Time" value={form.alarm_glue} onChange={(v) => setForm({ ...form, alarm_glue: v })} />
          </div>
          <TextArea label="Description" value={form.alarm_description} onChange={(v) => setForm({ ...form, alarm_description: v })} />
          <TextArea label="Cause" value={form.alarm_cause} onChange={(v) => setForm({ ...form, alarm_cause: v })} />
          <TextArea
            label="Corrective Measure"
            value={form.alarm_corrective_measure}
            onChange={(v) => setForm({ ...form, alarm_corrective_measure: v })}
          />
          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={form.requires_acknowledgement}
              onChange={(e) => setForm({ ...form, requires_acknowledgement: e.target.checked })}
            />
            Requires acknowledgement
          </label>
          {(createMutation.isError || updateMutation.isError) && (
            <p className="text-sm text-error">
              {(createMutation.error ?? updateMutation.error) instanceof Error
                ? (createMutation.error ?? updateMutation.error)!.message
                : 'Save failed.'}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!form.alarm_real_name || !form.alarm_code_name || !form.alarm_severity_id}
              className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditingId(null)}
              className="rounded-full border border-nav-line px-5 py-2 text-sm text-text-primary hover:bg-nav-hover"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      </div>

      <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-2xl border border-nav-line bg-card">
        <table className="w-full text-[13.5px]">
          <thead className="sticky top-0 z-10 bg-card">
            <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
              <th className="px-4 py-3.5 font-semibold">Alarm Name</th>
              <th className="px-4 py-3.5 font-semibold">Code</th>
              <th className="px-4 py-3.5 font-semibold">Severity</th>
              <th className="whitespace-nowrap px-4 py-3.5 font-semibold">Ack Required</th>
              {canManage && <th className="px-4 py-3.5 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((topic) => (
              <Fragment key={topic.alarm_topic_id}>
                <tr
                  className="cursor-pointer border-b border-nav-line last:border-0 hover:bg-nav-hover"
                  onClick={() => setExpandedId(expandedId === topic.alarm_topic_id ? null : topic.alarm_topic_id)}
                >
                  <td className="px-4 py-3.5 text-ink">{topic.alarm_real_name}</td>
                  <td className="px-4 py-3.5 text-ink">{topic.alarm_code_name}</td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-ink">{topic.alarm_severity_name}</td>
                  <td className="px-4 py-3.5 text-ink">{topic.requires_acknowledgement ? 'Required' : '—'}</td>
                  {canManage && (
                    <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => startEdit(topic)} className="text-xs text-accent hover:underline">
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Delete alarm topic "${topic.alarm_real_name}"?`)) {
                              deleteMutation.mutate(topic.alarm_topic_id)
                            }
                          }}
                          className="text-xs text-error hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
                {expandedId === topic.alarm_topic_id && (
                  <tr className="border-b border-nav-line bg-bg-secondary last:border-0">
                    <td colSpan={canManage ? 5 : 4} className="px-4 py-3 text-xs text-text-secondary">
                      {topic.alarm_description && <p className="mb-1"><strong>Description:</strong> {topic.alarm_description}</p>}
                      {topic.alarm_cause && <p className="mb-1"><strong>Cause:</strong> {topic.alarm_cause}</p>}
                      {topic.alarm_corrective_measure && (
                        <p><strong>Corrective Measure:</strong> {topic.alarm_corrective_measure}</p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold text-nav-muted">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
      />
    </div>
  )
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold text-nav-muted">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
      />
    </div>
  )
}
